<script>
  import Icon from "@iconify/svelte";
  import serverIcon from "@iconify/icons-mdi/cog";
  import tagIcon from "@iconify/icons-mdi/tag-outline";

  export let setup = null;
  export let setupErr = "";

  function item(label, value, hint = "") {
    return { label, value, hint };
  }

  function section(title, items) {
    return { title, items };
  }

  function pretty(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "—";
  }

  function groupEnvVars(envVars = []) {
    const grouped = new Map();
    for (const env of envVars) {
      const key = (env?.name || "General").toString();
      const group = key.includes("_") ? key.split("_")[0] : "General";
      if (!grouped.has(group)) grouped.set(group, []);
      grouped.get(group).push(env);
    }
    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, items]) => ({ group, items }));
  }

  $: sections = [
    section("Storage Connection", [
      item("Engine", pretty(setup?.engine)),
      item("Buckets", pretty(setup?.buckets?.length || 0)),
      item("Downloads", setup?.allow_download ? "Enabled" : "Disabled"),
      item("Renames", setup?.allow_rename ? "Enabled" : "Disabled"),
      item("Deletes", setup?.allow_delete ? "Enabled" : "Disabled"),
      item("Auth", setup?.auth_enabled ? "Enabled" : "Disabled")
    ]),
    ...groupEnvVars(setup?.env_vars || []).map((group) =>
      section(
        group.group,
        group.items.map((env) =>
          item(
            env.name,
            pretty(env.value || env.default),
            env.value ? "" : env.default ? "default" : ""
          )
        )
      )
    )
  ];

  const mono =
    "font-mono text-xs text-zinc-200 whitespace-nowrap overflow-hidden text-ellipsis";
  const sectionCard = "rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5";
  const cell = "rounded-xl border border-white/10 bg-white/[0.03] p-3 min-w-0";
  const klabel = "text-xs text-zinc-400 flex flex-wrap items-center gap-2";
  const khint =
    "text-[11px] text-zinc-500 inline-flex items-center gap-1 whitespace-nowrap";
</script>

<main class="w-full px-4 sm:px-6 mt-6">
  <section class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex items-start gap-3">
        <span
          class="inline-flex h-9 w-9 items-center justify-center border border-lime-400/60 bg-lime-400/60 text-lime-100"
        >
          <Icon icon={serverIcon} width="18" height="18" />
        </span>

        <div>
          <h1 class="text-lg font-normal tracking-wide text-zinc-100">
            Settings
          </h1>
          <p class="mt-0.5 text-sm text-zinc-400">
            How this instance is configured
          </p>
        </div>
      </div>
    </div>

    <!-- Error / Loading -->
    {#if setupErr}
      <div
        class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100 flex items-center gap-2"
      >
        <span class="break-words">{setupErr}</span>
      </div>
    {/if}

    {#if !setup}
      <div
        class="mt-6 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400"
      >
        Loading…
      </div>
    {:else}
      <!-- Sections -->
      <div class="mt-6 grid grid-cols-1 gap-4">
        {#each sections as s (s.title)}
          <div class={sectionCard}>
            <div
              class="flex items-baseline justify-between gap-3 border-b border-white/10 pb-3"
            >
              <div class="flex items-center gap-2">
                <Icon icon={tagIcon} width="16" height="16" />
                <div class="text-sm font-semibold tracking-wide text-white">
                  {s.title}
                </div>
              </div>

              <div class="text-xs text-zinc-500">{s.items.length} items</div>
            </div>

            <div
              class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {#each s.items as it (it.label)}
                <div class={cell}>
                  <div class={klabel}>
                    <span>{it.label}</span>
                    {#if it.hint}
                      <span class={khint}>• {it.hint}</span>
                    {/if}
                  </div>

                  <div class={`${mono} mt-2`} title={it.value}>
                    {it.value}
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</main>
